/**
 * useChargeLane - can the actor charge the target from where they stand, and where would the run end
 * (engine/battlefield/charge, 2026-10-21)?
 *
 * Read off the map the rules read (`ruleTokens`, so nothing with positions off) and only where the map may decide
 * at all (the live map's reach gap: none when the table's map modifiers are off) - otherwise there is no plan and
 * nothing is refused, and a Charge swings as it always did. The band is the movement as the conditions leave it,
 * behind the same house rules as the map's own bands; the ground is what this screen may see (the GM's includes
 * what is hidden). A horde is charged as the strike measures it: at its token (relations `reachGap`).
 *
 * @param {object} s - the shell: actor, activeTab, tokens (the rule tokens), terrain, mapSolids, isGm,
 *   ignoreTerrain, isMelee, effectiveSlot, liveMap, applyConditionModifiers, permissions
 * @returns {{ plan: object | null, band: number, refusal: string | null }}
 */
import { useMemo } from 'react';
import { chargeBand, planCharge } from '../engine/battlefield/charge';
import { terrainAt } from '../engine/battlefield/movement';
import { surfaceHeightAt } from '../engine/battlefield/reconcile';
import { boundaryContains } from '../engine/battlefield/boundary';
import { weaponReach } from '../engine/battlefield/reach';
import { terrainRulesFor, traitIdsOf } from '../engine/battlefield/traitRules';
import { movementUnder } from '../engine/conditionMovement';
import { projectDefender } from '../engine/roster/projection';
import { resolveFatigueSlowsMovement } from '../engine/settings/resolve';
import { groundAtFor } from './useBattlefieldSync';

const NO_PLAN = { plan: null, band: 0, refusal: null };

/** The ground a charge runs over, as this screen may see it. */
function groundFor(terrain, solids, { isGm, rules, ignoreTerrain }) {
  const groundAt = groundAtFor(terrain);
  return {
    terrainAt: (x, z, atHeight) => terrainAt(x, z, solids, { atHeight, includeHidden: isGm }),
    surfaceAt: (x, z, fromY) => surfaceHeightAt(x, z, solids, groundAt(x, z), { from: fromY }),
    inBounds: (x, z) => !terrain?.boundary || boundaryContains(terrain.boundary, x, z),
    maxStep: rules.maxStep,
    free: !!ignoreTerrain || rules.ignoresTerrain,
  };
}

/** How far the actor charges, as their conditions leave them (the map's own bands' house rules). */
function bandOf(actor, { applyConditionModifiers, permissions }) {
  const conditions = projectDefender(actor)?.conditions ?? [];
  const movement = applyConditionModifiers
    ? movementUnder(actor.sheet?.movement, conditions, { fatigueSlows: resolveFatigueSlowsMovement(permissions) })
        .movement
    : actor.sheet?.movement;
  return chargeBand(movement, conditions);
}

/** The plan, off the map as it stands: pure but for the house rules it is handed. */
function laneOf(a) {
  const from = a.actor ? a.tokens?.[a.actor.id] : null;
  const to = a.tokens?.[a.activeTab];
  if (!a.isMelee || !a.mayDecide || !from || !to) return NO_PLAN;
  const { metres, refusal } = bandOf(a.actor, a);
  if (refusal) return { plan: null, band: metres, refusal };
  const rules = terrainRulesFor(traitIdsOf(a.actor.sheet), projectDefender(a.actor)?.size);
  const ground = groundFor(a.terrain, a.mapSolids ?? [], { isGm: a.isGm, rules, ignoreTerrain: a.ignoreTerrain });
  const plan = planCharge(from, to, {
    band: metres,
    reach: weaponReach(a.effectiveSlot),
    ground,
  });
  return { plan, band: metres, refusal: plan.ok ? null : plan.reason };
}

/** Over the length target on purpose: its body is one memo and the values it is keyed on (declarative). */
export default function useChargeLane(s) {
  const { actor, activeTab, tokens, terrain, mapSolids, isGm, ignoreTerrain, isMelee, effectiveSlot } = s;
  const { applyConditionModifiers, permissions } = s;
  const mayDecide = s.liveMap?.reachGap !== null && s.liveMap?.reachGap !== undefined;
  return useMemo(
    () =>
      laneOf({
        actor,
        activeTab,
        tokens,
        terrain,
        mapSolids,
        isGm,
        ignoreTerrain,
        isMelee,
        effectiveSlot,
        mayDecide,
        applyConditionModifiers,
        permissions,
      }),
    [
      actor,
      activeTab,
      tokens,
      terrain,
      mapSolids,
      isGm,
      ignoreTerrain,
      isMelee,
      effectiveSlot,
      mayDecide,
      applyConditionModifiers,
      permissions,
    ],
  );
}
